import React, { RefObject, useCallback, useMemo, useRef, useState } from "react";
import type { option, hanldeSlideOutMenu } from "../interfaces";
import { handleExpandedMenu } from "../../TopNavigator/ExpandedMenu/utils";
import Service, { useFormServiceProps } from "../Service/Service";
import { useSmartNavigate } from "../../../Universal/API/Context-API/hooks/useSmartNavigate";

const CLOSE_DELAY_MS = 350;

export const useOptions = ({ navigation }: { navigation: any, refExpandedMenu }) => {
  const refSlideOutMenu = useRef<hanldeSlideOutMenu>(null);

  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hoverStateRef = useRef({
    overMenuBar: false,
    overPanel: false,
  });

  

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const [serviceMobile, setServiceMobile] = useState<option[] | null>(null)

  const scheduleCloseIfNeeded = useCallback(
    (refExpandedMenu: RefObject<handleExpandedMenu | null>) => {
      clearCloseTimer();

      closeTimerRef.current = setTimeout(() => {
        const { overMenuBar, overPanel } = hoverStateRef.current;

        if (!overMenuBar && !overPanel) {
          refExpandedMenu.current?.close();
        }
      }, CLOSE_DELAY_MS);
    },
    [clearCloseTimer]
  );

  // 👇 ВЕШАЕШЬ НА ROOT Pressable TopNavigator
  const menuBarHover = useMemo(
    () => ({
      onHoverIn: () => {
        hoverStateRef.current.overMenuBar = true;
        clearCloseTimer();
      },
      onHoverOut: (refExpandedMenu: RefObject<handleExpandedMenu | null>) => {
        hoverStateRef.current.overMenuBar = false;
        scheduleCloseIfNeeded(refExpandedMenu);
      },
    }),
    [clearCloseTimer, scheduleCloseIfNeeded]
  );

  // 👇 ПЕРЕДАЕШЬ В Service
  const makePanelHover = useCallback(
    (refExpandedMenu: RefObject<handleExpandedMenu | null>) => ({
      onPanelHoverIn: () => {
        hoverStateRef.current.overPanel = true;
        clearCloseTimer();
      },
      onPanelHoverOut: () => {
        hoverStateRef.current.overPanel = false;
        scheduleCloseIfNeeded(refExpandedMenu);
      },
    }),
    [clearCloseTimer, scheduleCloseIfNeeded]
  );

  const {smartNavigate} = useSmartNavigate({navigation})

  const { formServiceProps } = useFormServiceProps({ navigation });

  const optionsFunction = useCallback(
    ({ variant = "top" }: { variant: "top" | "left" }): option[] => [
      {
        text: "О бренде",
        onPress: () => {
          refSlideOutMenu.current?.setDoAnimation(false);
          smartNavigate({ navigation, goTo: "About" });
        },
      },
      {
        text: "Коллекции",
        onPress: () => {
          refSlideOutMenu.current?.setDoAnimation(false);
          smartNavigate({ navigation, goTo: "Collections" });
        },
      },
      {
        text: "Каталог",
        onPress: () => {
          refSlideOutMenu.current?.setDoAnimation(false);
          // if (navigation.getCurrentRoute?.()?.name === "Catalog") return;
          smartNavigate({ navigation, goTo: "Catalog", replace: true });
        },
      },
      {
        text: "Где купить",
        onPress: () => {
          refSlideOutMenu.current?.setDoAnimation(false);
          smartNavigate({ navigation, goTo: "WhereToBuy" });
        },
      },
      {
        text: "Контакты",
        onPress: () => {
          refSlideOutMenu.current?.setDoAnimation(false);
          smartNavigate({ navigation, goTo: "Contacts" });
        },
      },
      {
        text: "Сервис",
        onPress: (refExpandedMenu: RefObject<handleExpandedMenu | null>) => {
          if (!refExpandedMenu?.current) {
      // Фоллбек для mobile: можно просто перейти на "Account" / отдельный экран "Service"
            setServiceMobile([
              ...formServiceProps({navigation, refExpandedMenu, close:  refSlideOutMenu.current?.close}),
              {
                text: 'back',
                onPress: () => {
                  setServiceMobile(null)
                  refSlideOutMenu.current?.setDoAnimation(true);
                }
              }
            ])
            refSlideOutMenu.current?.setDoAnimation(true);
            return;
          }
          refExpandedMenu.current?.openClose(
            <Service
              navigation={navigation}
              refExpandedMenu={refExpandedMenu}
              hover={makePanelHover(refExpandedMenu)}
            />
          );
        },
      },
    ],
    [makePanelHover, navigation, formServiceProps]
  );

  return { optionsFunction, refSlideOutMenu, menuBarHover, serviceMobile, setServiceMobile};
};
